import { defineStore } from 'pinia'
import { reactive, ref } from 'vue'
import storeConfigs from 'constants/storeConfigs.ts'
import type { Anchor } from 'vuetify/lib/util/anchor.d.ts'

interface ToastButton {
	text: string
	on?: () => void
	color?: string
	dataTest?: string
}

type ToastMessage = string | { key: string; params?: Record<string, unknown> }

export interface ToastOptions {
	closeOnEscape?: boolean
	displayMode?: 'once' | 'replace' | 'queue'
	header?: string
	message: ToastMessage
	is_html?: boolean
	onClosed?: () => void
	position?: Anchor
	timeout?: number | null
	color?: string
	maxWidth?: string | number
	buttons?: ToastButton[]
	theme?: 'light' | 'dark'
}

export const useToastStore = defineStore(storeConfigs.toast.name, () => {
	const isVisible = ref(false)
	const queue = ref<ToastOptions[]>([])
	const currentToast = reactive<ToastOptions>({
		message: '',
		header: '',
		timeout: 5000,
		position: 'center center',
		color: 'success',
		displayMode: 'replace',
		closeOnEscape: true,
	})

	// Actions
	function showToast(options: ToastOptions) {
		const toast = {
			...currentToast,
			...options,
		}

		if (toast.displayMode === 'queue' && isVisible.value) {
			queue.value.push(toast)
			return
		}

		// If replace mode or no toast is showing, show immediately
		Object.assign(currentToast, toast)
		isVisible.value = true
	}

	function hideToast() {
		isVisible.value = false

		setTimeout(() => {
			if (typeof currentToast.onClosed === 'function') {
				currentToast.onClosed()
			}
		}, 0)

		// Process queue if any toasts are waiting
		setTimeout(() => {
			if (queue.value.length > 0) {
				const nextToast = queue.value.shift()
				if (nextToast) {
					showToast(nextToast)
				}
			}
		}, 300)
	}

	function toastError(options: Partial<ToastOptions> = {}) {
		const buttons: ToastButton[] = [
			{
				text: options.buttons?.[0]?.text || 'OK',
				on: options.buttons?.[0]?.on || (() => hideToast()),
				dataTest: 'close',
				color: options.buttons?.[0]?.color || 'grey-lighten-2',
			},
		]
		showToast({
			message:
				options.message ||
				'Es ist ein Fehler aufgetreten, versuchen Sie es bitte erneut.',
			header: options.header || '',
			is_html: options.is_html || false,
			position: options.position,
			timeout: options.timeout === undefined ? null : options.timeout,
			buttons,
			color: 'red',
			closeOnEscape:
				options.closeOnEscape !== undefined ? options.closeOnEscape : true,
			displayMode: options.displayMode || 'replace',
			onClosed: options.onClosed,
			maxWidth: options.maxWidth,
		})
	}

	function toastSuccess(options: Partial<ToastOptions> = {}) {
		const buttons: ToastButton[] = [
			{
				text: options.buttons?.[0]?.text || 'OK',
				on: options.buttons?.[0]?.on || (() => hideToast()),
				dataTest: 'close',
				color: options.buttons?.[0]?.color || 'grey-lighten-2',
			},
		]

		showToast({
			message: options.message || '',
			header: options.header || '',
			is_html: options.is_html || false,
			position: options.position,
			color: 'green',
			timeout: options.timeout === undefined ? 3000 : options.timeout,
			buttons,
			displayMode: options.displayMode || 'once',
			maxWidth: options.maxWidth,
			onClosed: options.onClosed,
		})
	}

	function toastInfo(options: Partial<ToastOptions> = {}) {
		const buttons: ToastButton[] = [
			{
				text: options.buttons?.[0]?.text || 'OK',
				on: options.buttons?.[0]?.on || (() => hideToast()),
				color: options.buttons?.[0]?.color || 'grey-lighten-2',
				dataTest: 'close',
			},
		]
		showToast({
			message: options.message || '',
			header: options.header || '',
			is_html: options.is_html || false,
			position: options.position,
			color: 'blue',
			timeout: options.timeout === undefined ? 5000 : options.timeout,
			buttons,
			displayMode: options.displayMode || 'once',
			maxWidth: options.maxWidth,
			onClosed: options.onClosed,
		})
	}

	function toastQuestion(
		options: {
			message: ToastMessage
			is_html?: boolean
			header?: string
			confirm?: Partial<ToastButton>
			deny?: Partial<ToastButton>
			cancel?: (Partial<ToastButton> & { text?: string }) | false
			showCancelButton?: boolean
			color?: string
			maxWidth?: string | number
			displayMode?: 'once' | 'replace' | 'queue'
			onClosed?: () => void
		} = { message: '' }
	) {
		const buttons: ToastButton[] = [
			{
				text: options.confirm?.text || 'common.yes' + '',
				on: options.confirm?.on,
				color: options.deny?.color || 'red',
				dataTest: 'confirm',
			},
			{
				text: options.deny?.text || 'common.no',
				on: options.deny?.on,
				color: options.deny?.color || 'grey',
				dataTest: 'deny',
			},
		]

		const showCancel =
			options.showCancelButton === true ||
			(options.cancel && options.cancel !== false)

		if (showCancel) {
			const cancelButton = options.cancel || {}
			buttons.push({
				text: cancelButton.text || 'Abbrechen',
				on: cancelButton.on,
				color: cancelButton.color || 'grey-lighten-2',
				dataTest: 'cancel',
			})
		}

		showToast({
			message: options.message,
			is_html: options.is_html || false,
			header: options.header || '',
			color: options.color || 'blue',
			timeout: null,
			buttons,
			theme: 'dark',
			displayMode: options.displayMode || 'replace',
			maxWidth: options.maxWidth,
			onClosed: options.onClosed,
		})
	}

	return {
		// State
		isVisible,
		currentToast,

		// Actions
		showToast,
		hideToast,
		toastError,
		toastSuccess,
		toastInfo,
		toastQuestion,
	}
})
